// Re-export all vendor utils
export * from '@salesforce/retail-react-app/app/utils/utils';

/**
 * Ensures a value is an array, returns empty array if not.
 */
export const ensureArray = (value) => {
  return Array.isArray(value) ? value : [];
};

/**
 * Checks if a value is a non-empty array.
 */
export const hasItems = (value) => {
  return Array.isArray(value) && value.length > 0;
};

/**
 * Generate URL-friendly slug from a string (e.g., store name, page title)
 * Used for creating SEO-friendly URLs like /showroom/sydney-cbd
 *
 * @param {string} text - The text to convert to a slug
 * @returns {string} URL-friendly slug
 *
 * @example
 * generateSlug('Sydney CBD') // returns 'sydney-cbd'
 * generateSlug('Annandale Outlet') // returns 'annandale-outlet'
 * generateSlug('King Living Melbourne') // returns 'king-living-melbourne'
 * generateSlug("King's & Store (Main)") // returns 'king-s-store-main'
 */
export const generateSlug = (text) => {
  if (!text) return '';

  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '-') // Replace special characters with hyphens
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
};

/**
 * Generate cookie name for "My Showroom" based on site ID
 * Each region/site has its own cookie to store the user's preferred showroom
 *
 * @param {string} siteId - The site identifier (e.g., 'kl-au', 'kl-nz', 'kl-uk')
 * @returns {string} Cookie name in format: my_showroom_{siteId}
 *
 * @example
 * getMyShowroomCookieName('kl-au') // returns 'my_showroom_kl-au'
 * getMyShowroomCookieName('kl-nz') // returns 'my_showroom_kl-nz'
 * getMyShowroomCookieName('kl-uk') // returns 'my_showroom_kl-uk'
 */
export const getMyShowroomCookieName = (siteId) => {
  if (!siteId) return 'my_showroom';

  return `my_showroom_${siteId}`;
};

/**
 * Generate cookie name for "Nearest Showroom" based on site ID
 * Stores the nearest showroom based on user's current location (cached)
 *
 * @param {string} siteId - The site identifier (e.g., 'kl-au', 'kl-nz', 'kl-uk')
 * @returns {string} Cookie name in format: nearest_showroom_{siteId}
 *
 * @example
 * getNearestShowroomCookieName('kl-au') // returns 'nearest_showroom_kl-au'
 * getNearestShowroomCookieName('kl-nz') // returns 'nearest_showroom_kl-nz'
 */
export const getNearestShowroomCookieName = (siteId) => {
  if (!siteId) return 'nearest_showroom';

  return `nearest_showroom_${siteId}`;
};

// =============================================================================
// MY SHOWROOM - Custom Event for cross-component communication
// =============================================================================

export const SHOWROOM_CHANGED_EVENT = 'kl:showroom-changed';

/**
 * Dispatch custom event when showroom selection changes
 * This allows components like Header to update without page reload
 * @param {Object|null} showroom - The new showroom data or null if cleared
 */
export const dispatchShowroomChangedEvent = (showroom) => {
  if (typeof window === 'undefined') return;

  window.dispatchEvent(new CustomEvent(SHOWROOM_CHANGED_EVENT, {
    detail: {showroom},
  }));
};

// =============================================================================
// MY SHOWROOM - Cookie Helpers (7 days TTL)
// =============================================================================

const MY_SHOWROOM_COOKIE_TTL_DAYS = 7;

/**
 * Get user's preferred showroom from cookie
 * @param {string} siteId - The site identifier
 * @returns {Object|null} Showroom data {id, name, slug, lat, lng} or null
 */
export const getMyShowroomFromCookie = (siteId) => {
  if (typeof document === 'undefined') return null;

  const cookieName = getMyShowroomCookieName(siteId);

  try {
    const cookie = document.cookie
      .split('; ')
      .find((row) => row.startsWith(`${cookieName}=`));

    if (cookie) {
      const value = cookie.split('=')[1];

      return JSON.parse(decodeURIComponent(value));
    }
  } catch (error) {
    console.error('[MyShowroom] Error reading cookie:', error);
  }

  return null;
};

/**
 * Save user's preferred showroom to cookie (7 days TTL)
 * Also dispatches a custom event for real-time UI updates
 * @param {string} siteId - The site identifier
 * @param {Object} showroom - Showroom data to save
 */
export const setMyShowroomToCookie = (siteId, showroom) => {
  if (typeof document === 'undefined' || !showroom) return;

  const cookieName = getMyShowroomCookieName(siteId);

  try {
    const value = encodeURIComponent(JSON.stringify(showroom));
    const expires = new Date();

    expires.setDate(expires.getDate() + MY_SHOWROOM_COOKIE_TTL_DAYS);

    document.cookie = `${cookieName}=${value}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;

    // Dispatch event for real-time UI updates (e.g., Header)
    dispatchShowroomChangedEvent(showroom);
  } catch (error) {
    console.error('[MyShowroom] Error saving cookie:', error);
  }
};

/**
 * Remove user's preferred showroom cookie
 * @param {string} siteId - The site identifier
 */
export const clearMyShowroomCookie = (siteId) => {
  if (typeof document === 'undefined') return;

  const cookieName = getMyShowroomCookieName(siteId);

  document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
};

// =============================================================================
// SHOWROOM POPUP - sessionStorage Helpers
// =============================================================================

/**
 * Get sessionStorage key for popup dismissed state
 * @param {string} siteId - The site identifier
 * @returns {string} Key name
 */
export const getPopupDismissedKey = (siteId) => {
  if (!siteId) return 'kl_showroom_popup_dismissed';

  return `kl_showroom_popup_dismissed_${siteId}`;
};

/**
 * Check if showroom popup was dismissed this session
 * @param {string} siteId - The site identifier
 * @returns {boolean}
 */
export const isShowroomPopupDismissed = (siteId) => {
  if (typeof sessionStorage === 'undefined') return false;

  return sessionStorage.getItem(getPopupDismissedKey(siteId)) === 'true';
};

/**
 * Mark showroom popup as dismissed
 * @param {string} siteId - The site identifier
 */
export const setShowroomPopupDismissed = (siteId) => {
  if (typeof sessionStorage === 'undefined') return;

  sessionStorage.setItem(getPopupDismissedKey(siteId), 'true');
};

// =============================================================================
// LOCALE PREFERENCE - Cookie Helpers (365 days TTL)
// =============================================================================

const LOCALE_COOKIE_TTL_DAYS = 365;

export const LOCALE_CHANGED_EVENT = 'kl:locale-changed';

/**
 * Generate cookie name for preferred locale based on site ID
 * Each region/site has its own cookie to store the user's preferred locale
 *
 * @param {string} siteId - The site identifier (e.g., 'kl-au', 'kl-ca')
 * @returns {string} Cookie name in format: preferred_locale_{siteId}
 *
 * @example
 * getPreferredLocaleCookieName('kl-ca') // returns 'preferred_locale_kl-ca'
 */
export const getPreferredLocaleCookieName = (siteId) => {
  if (!siteId) return 'preferred_locale';

  return `preferred_locale_${siteId}`;
};

/**
 * Get user's preferred locale from cookie
 * @param {string} siteId - The site identifier
 * @returns {string|null} Locale ID (e.g., 'en-CA', 'fr-CA') or null
 */
export const getPreferredLocaleFromCookie = (siteId) => {
  if (typeof document === 'undefined') return null;

  const cookieName = getPreferredLocaleCookieName(siteId);

  try {
    const cookie = document.cookie
      .split('; ')
      .find((row) => row.startsWith(`${cookieName}=`));

    if (cookie) {
      const value = cookie.split('=')[1];

      return decodeURIComponent(value);
    }
  } catch (error) {
    console.error('[PreferredLocale] Error reading cookie:', error);
  }

  return null;
};

/**
 * Save user's preferred locale to cookie (365 days TTL)
 * Also dispatches a custom event for real-time UI updates
 * @param {string} siteId - The site identifier
 * @param {string} localeId - Locale ID to save (e.g., 'en-CA', 'fr-CA')
 */
export const setPreferredLocaleToCookie = (siteId, localeId) => {
  if (typeof document === 'undefined' || !localeId) return;

  const cookieName = getPreferredLocaleCookieName(siteId);

  try {
    const value = encodeURIComponent(localeId);
    const expires = new Date();

    expires.setDate(expires.getDate() + LOCALE_COOKIE_TTL_DAYS);

    document.cookie = `${cookieName}=${value}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;

    // Dispatch event for real-time UI updates
    dispatchLocaleChangedEvent(localeId);
  } catch (error) {
    console.error('[PreferredLocale] Error saving cookie:', error);
  }
};

/**
 * Remove user's preferred locale cookie
 * @param {string} siteId - The site identifier
 */
export const clearPreferredLocaleCookie = (siteId) => {
  if (typeof document === 'undefined') return;

  const cookieName = getPreferredLocaleCookieName(siteId);

  document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
};

/**
 * Dispatch custom event when locale preference changes
 * This allows components to react to locale changes without page reload
 * @param {string} localeId - The new locale ID
 */
export const dispatchLocaleChangedEvent = (localeId) => {
  if (typeof window === 'undefined') return;

  window.dispatchEvent(new CustomEvent(LOCALE_CHANGED_EVENT, {
    detail: {localeId},
  }));
};